import { useCallback, useEffect, useMemo, useState } from 'react'

import { api } from '../api'
import { formatWhen, t } from '../i18n'
import { haptic, notify, openExternal, useBackButton } from '../telegram'
import type { Booking, BookingSlots } from '../types'

export interface BookTarget {
  clubId: number
  /** Корт под игру Por4: капитан приходит с экрана игры */
  gameId?: number
  startsAt?: string
  minutes?: number
}

interface Props {
  target: BookTarget
  onClose: () => void
  onMine: () => void
}

/** Сумма в евро: «16 €», «10,67 €» */
export function euro(value: number | null | undefined): string {
  if (value == null) return ''
  const text = Number.isInteger(value) ? String(value) : value.toFixed(2)
  return `${t().locale.startsWith('en') ? text : text.replace('.', ',')} €`
}

/** Дата клуба ГГГГ-ММ-ДД для дня (по времени устройства — игроки и клубы в одной зоне) */
function isoDay(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function dayLabel(d: Date, index: number): string {
  if (index === 0) return t().bookToday
  if (index === 1) return t().bookTomorrow
  const date = d.toLocaleDateString(t().locale, { day: '2-digit', month: '2-digit' })
  return `${t().weekdays[d.getDay()]} ${date}`
}

function useScrollLock() {
  useEffect(() => {
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previous
    }
  }, [])
}

/** Экран заявки на корт (v0.35): день → длительность → корт → время → заявка.
 *  Клуб подтверждает, оплата — переводом по реквизитам после подтверждения. */
export default function BookingScreen({ target, onClose, onMine }: Props) {
  useEffect(() => useBackButton(onClose), [onClose])
  useScrollLock()

  const initialDay = target.startsAt ? isoDay(new Date(target.startsAt)) : isoDay(new Date())
  const [day, setDay] = useState(initialDay)
  const [minutes, setMinutes] = useState<number | null>(target.minutes ?? null)
  const [court, setCourt] = useState<number | null>(null)
  const [data, setData] = useState<BookingSlots | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [picked, setPicked] = useState<string | null>(target.startsAt ?? null)
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [sent, setSent] = useState<Booking | null>(null)

  const load = useCallback(async () => {
    try {
      const res = await api.bookingSlots(target.clubId, minutes, day)
      // Длительность игры не из сетки клуба — берём ближайшую из тех, что есть
      if (minutes !== null && !res.club.durations.includes(minutes)) {
        const near = res.club.durations.reduce((a, b) => (Math.abs(b - minutes) < Math.abs(a - minutes) ? b : a))
        setMinutes(near)
        return
      }
      if (minutes === null) setMinutes(res.minutes)
      setData(res)
      setError(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ошибка')
    }
  }, [target.clubId, minutes, day])

  useEffect(() => {
    void load()
  }, [load])

  // Пока экран открыт, занятость меняется (другие игроки, календарь клуба)
  useEffect(() => {
    const id = window.setInterval(() => void load(), 30000)
    return () => window.clearInterval(id)
  }, [load])

  const club = data?.club
  // Выбранный день (например, день игры) может быть за краем ленты дат — прокручиваем к нему
  const hasData = data !== null
  useEffect(() => {
    if (!hasData) return
    document.querySelector('.chat-list.book .chips [aria-pressed="true"]')?.scrollIntoView({ inline: 'center', block: 'nearest' })
  }, [hasData, day])
  const days = useMemo(() => {
    const n = club?.horizon_days ?? 14
    const today = new Date()
    return Array.from({ length: n }, (_, i) => {
      const d = new Date(today.getFullYear(), today.getMonth(), today.getDate() + i)
      return { iso: isoDay(d), label: dayLabel(d, i) }
    })
  }, [club?.horizon_days])

  const slots = data?.slots ?? []
  const fits = (free: number[]) => (court === null ? free.length > 0 : free.includes(court))
  const chosen = slots.find((s) => s.at === picked) ?? null
  const chosenOk = chosen !== null && fits(chosen.free)
  const price = club?.prices.find((p) => p.minutes === (data?.minutes ?? minutes))?.amount ?? null
  const anyFree = slots.some((s) => fits(s.free))

  async function send() {
    if (!chosen || !club || !data) return
    setBusy(true)
    haptic('medium')
    try {
      const booking = await api.createBooking({
        club_id: club.id,
        starts_at: chosen.at,
        minutes: data.minutes,
        court,
        note: note.trim() || null,
        game_id: target.gameId ?? null,
      })
      setSent(booking)
    } catch (e) {
      notify(e instanceof Error ? e.message : 'Ошибка')
      setPicked(null)
      void load()
    } finally {
      setBusy(false)
    }
  }

  const end = chosen && data ? new Date(new Date(chosen.at).getTime() + data.minutes * 60000) : null
  const endLabel = end ? end.toLocaleTimeString(t().locale, { hour: '2-digit', minute: '2-digit' }) : ''

  return (
    <div className="chat-screen" role="dialog" aria-modal="true" aria-label={t().bookCourt}>
      <div className="chat-head">
        <button className="ghost chat-back" onClick={onClose} aria-label={t().back}>
          ←
        </button>
        <div className="title chat-title">🎾 {club ? t().bookTitle(club.name) : t().bookCourt}</div>
      </div>
      <div className="chat-list book">
        {error && !data && <div className="card muted">{error}</div>}
        {!data && !error && <p className="muted">{t().loading}</p>}

        {sent && club ? (
          <div className="card book-sent">
            <div className="big">✅ {t().bookSent}</div>
            <div style={{ marginTop: 8 }}>
              <b>{formatWhen(sent.starts_at)}</b>–{new Date(sent.ends_at).toLocaleTimeString(t().locale, { hour: '2-digit', minute: '2-digit' })} · {sent.court_name}
            </div>
            <div className="muted" style={{ marginTop: 4 }}>
              {club.name} · {euro(sent.amount)}
            </div>
            {sent.reply_by && <p style={{ marginTop: 10 }}>{t().bookSentHint(formatWhen(sent.reply_by))}</p>}
            <div className="row" style={{ gap: 8, marginTop: 12 }}>
              <button style={{ flex: 1 }} onClick={onMine}>
                {t().bookMine}
              </button>
              <button className="ghost" style={{ flex: 1 }} onClick={onClose}>
                {t().bookDone}
              </button>
            </div>
          </div>
        ) : (
          club &&
          data && (
            <>
              <div className="card">
                <div className="row between" style={{ alignItems: 'flex-start' }}>
                  <div style={{ minWidth: 0 }}>
                    <div className="title">{club.name}</div>
                    <div className="muted">
                      {club.city}
                      {club.address ? ` · ${club.address}` : ''}
                    </div>
                  </div>
                  {club.maps_url && (
                    <button className="ghost book-route" onClick={() => openExternal(club.maps_url!)}>
                      📍 {t().bookRoute}
                    </button>
                  )}
                </div>
                <div className="book-prices">
                  {club.prices
                    .filter((p) => p.amount != null)
                    .map((p) => (
                      <span key={p.minutes}>
                        {t().bookHours(p.minutes)} — <b>{euro(p.amount)}</b>
                      </span>
                    ))}
                  {club.price_note && <span className="muted">({club.price_note})</span>}
                </div>
                {!club.public && club.is_manager && <div className="muted small" style={{ marginTop: 8 }}>🔒 {t().deskHidden}</div>}
              </div>

              {target.gameId && target.startsAt && (
                <div className="card book-for">{t().bookFor(formatWhen(target.startsAt))}</div>
              )}

              <div className="book-h">{t().bookDay}</div>
              <div className="chips scroll">
                {days.map((d) => (
                  <button
                    key={d.iso}
                    className="chip"
                    aria-pressed={d.iso === day}
                    onClick={() => {
                      setDay(d.iso)
                      setPicked(null)
                    }}
                  >
                    {d.label}
                  </button>
                ))}
              </div>

              <div className="book-h">{t().bookDuration}</div>
              <div className="seg book-seg" style={{ gridTemplateColumns: `repeat(${club.durations.length}, 1fr)` }}>
                {club.durations.map((m) => (
                  <button
                    key={m}
                    aria-pressed={m === data.minutes}
                    onClick={() => {
                      setMinutes(m)
                      setPicked(null)
                    }}
                  >
                    {t().bookHours(m)}
                    {club.prices.find((p) => p.minutes === m)?.amount != null && (
                      <span className="book-seg-price">{euro(club.prices.find((p) => p.minutes === m)!.amount)}</span>
                    )}
                  </button>
                ))}
              </div>

              {club.courts.length > 1 && (
                <>
                  <div className="book-h">{t().bookCourtPick}</div>
                  <div className="seg book-seg" style={{ gridTemplateColumns: `repeat(${club.courts.length + 1}, 1fr)` }}>
                    <button aria-pressed={court === null} onClick={() => setCourt(null)}>
                      {t().bookAnyCourt}
                    </button>
                    {club.courts.map((name, i) => (
                      <button key={name} aria-pressed={court === i + 1} onClick={() => setCourt(i + 1)}>
                        {name}
                      </button>
                    ))}
                  </div>
                </>
              )}

              <div className="book-h">{t().bookTime}</div>
              {anyFree ? (
                <div className="book-grid">
                  {slots.map((s) => {
                    const ok = fits(s.free)
                    return (
                      <button
                        key={s.at}
                        className="chip book-time"
                        disabled={!ok}
                        aria-pressed={s.at === picked && ok}
                        onClick={() => {
                          haptic('light')
                          setPicked(s.at)
                        }}
                      >
                        {s.label}
                      </button>
                    )
                  })}
                </div>
              ) : (
                <div className="card muted">{t().bookNoTimes}</div>
              )}

              {chosenOk && chosen && (
                <div className="card book-summary">
                  <div className="title">
                    {formatWhen(chosen.at)}–{endLabel}
                  </div>
                  <div className="muted" style={{ marginTop: 2 }}>
                    {club.courts[(court ?? chosen.free[0]) - 1]}
                    {price != null && <> · <b className="book-sum">{euro(price)}</b></>}
                  </div>
                  <label className="muted small" style={{ display: 'block', marginTop: 12 }}>
                    {t().bookNote}
                  </label>
                  <input
                    value={note}
                    maxLength={200}
                    placeholder={t().bookNotePh}
                    onChange={(e) => setNote(e.target.value)}
                    style={{ marginTop: 4 }}
                  />
                  <p className="muted small" style={{ marginTop: 10 }}>
                    {t().bookRules(club.reply_hours, club.cancel_hours)}
                  </p>
                  <button style={{ width: '100%', marginTop: 4 }} disabled={busy} onClick={send}>
                    {t().bookSend}
                  </button>
                </div>
              )}

              <button className="ghost" style={{ width: '100%', marginTop: 6 }} onClick={onMine}>
                {t().bookMine}
              </button>
            </>
          )
        )}
      </div>
    </div>
  )
}
